'use client';

import type { ReactNode } from 'react';

import type { ImageType, PaginationMeta } from '@eliadmin/core/src/api/api-types';
import type { ProductIndexData } from '@eliadmin/core/src/api/products/types/product-types';
import type { ChipItem } from '@eliadmin/core/src/components/svetech/ui/chip';

import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import Link from '@eliadmin/core/src/components/link';
import { ProductListSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import { EmptyState } from '@eliadmin/core/src/components/svetech/listing/empty-state';
import { ButtonLink } from '@eliadmin/core/src/components/svetech/ui/button';
import { ChipList } from '@eliadmin/core/src/components/svetech/ui/chip';
import { CmsImage } from '@eliadmin/core/src/components/svetech/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { stripTags } from '@eliadmin/core/src/utils/svetech/text';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';

const CARD_RADIUS = 'rounded-3xl rounded-tr-none md:rounded-[32px] md:rounded-tr-none';

/** One group of results (Kategórie / Články / Produkty) with its count in the heading. */
export function ResultGroup({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4 md:gap-6">
      <Heading tag="h4" as="h2" title={`${title} (${count})`} />
      {children}
    </section>
  );
}

export type ResultCardData = {
  id: number;
  title: string;
  /** Plain text, clamped to two lines. */
  description: string;
  image: ImageType | null;
  href: string;
  /** Muted line above the title (article date). */
  meta?: ReactNode;
};

/**
 * Search result (Key design „Karta“): 3:2 thumbnail, card title and two lines of text; the title
 * link covers the card, the arrow only repeats it.
 */
export function ResultCard({ item }: { item: ResultCardData }) {
  return (
    <article
      className={cn(
        'group relative flex items-start gap-4 border border-[#dfe2db] bg-white p-4 md:items-center md:gap-6 md:p-6',
        CARD_RADIUS,
        'transition-colors hover:border-[#c9cdc4] motion-reduce:transition-none',
        'has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-[#2c7a25] has-[a:focus-visible]:ring-offset-2',
      )}
    >
      <div className="relative aspect-[3/2] w-24 shrink-0 overflow-hidden rounded-xl rounded-tr-none bg-[#f2f3ef] sm:w-36 md:w-48 md:rounded-[15px] md:rounded-tr-none">
        <CmsImage image={item.image} decorative sizes="(min-width: 768px) 192px, 144px" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 md:gap-2">
        {item.meta ? <p className="svt-body-s text-[#6b7681]">{item.meta}</p> : null}
        <h3 className="svt-title-card break-words text-[#14171a] transition-colors group-hover:text-[#2c7a25] motion-reduce:transition-none">
          <Link
            href={item.href}
            prefetch={false}
            className="after:absolute after:inset-0 focus-visible:outline-none"
          >
            {item.title}
          </Link>
        </h3>
        {item.description ? (
          <p className="svt-body line-clamp-2 text-[#4a5560]">{item.description}</p>
        ) : null}
      </div>
      <Icon
        name="arrow-right"
        className="hidden size-6 text-[#14171a] transition-colors group-hover:text-[#2c7a25] motion-reduce:transition-none md:block"
      />
    </article>
  );
}

export function ResultCards({ items }: { items: ResultCardData[] }) {
  return (
    <ul className="flex flex-col gap-3 md:gap-4">
      {items.map(item => (
        <li key={item.id}>
          <ResultCard item={item} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Product group of both search pages with the core list analytics (`view_item_list`), the
 * ItemList JSON-LD (search page) and the pagination of the product query.
 */
export function ProductResults({
  products,
  listId,
  withSchema = false,
  resetWhen,
}: {
  products: { data: ProductIndexData[]; meta?: PaginationMeta };
  /** `item_list_name` of the analytics event: `search` / `search_advanced`. */
  listId: string;
  withSchema?: boolean;
  resetWhen: unknown[];
}) {
  const { t } = useTranslations();
  const searchParams = useSearchParams();
  const currency = useGlobalStore(s => s.generalLayout?.currency);

  if (!products.data.length) return null;

  return (
    <ResultGroup title={t('text150')} count={products.meta?.total ?? products.data.length}>
      {withSchema ? <ProductListSchema products={products.data} currency={currency} /> : null}
      <TrackComponent
        eventName="view_item_list"
        eventParams={{
          item_list_id: `${listId}:${searchParams.get('search') ?? ''}`,
          item_list_name: listId,
        }}
        items={products.data}
      />
      <ResultCards
        items={products.data.map(product => ({
          id: product.id,
          title: product.name,
          description: stripTags(product.short_description ?? ''),
          image: product.image ?? null,
          href: ROUTES.PRODUCT({ slug: product.slug }),
        }))}
      />
      {(products.meta?.last_page ?? 1) > 1 ? (
        <Suspense>
          <Pagination
            links={products.meta?.links ?? []}
            resetWhen={resetWhen}
            className={{ main: 'justify-center pt-4' }}
          />
        </Suspense>
      ) : null}
    </ResultGroup>
  );
}

/** Nothing found: the way on are the main categories of the site and the homepage. */
export function NoResults({ categories }: { categories: ChipItem[] }) {
  const { t } = useTranslations();

  return (
    <EmptyState title={t('text752')} text={t('text2376')} className="py-4 md:py-8">
      <ChipList items={categories} className="justify-center" />
      <ButtonLink href="/" variant="text" icon="arrow-left">
        {t('text2031')}
      </ButtonLink>
    </EmptyState>
  );
}

export function ResultsSkeleton() {
  return (
    <div className="flex flex-col gap-3 md:gap-4" aria-hidden="true">
      <Skeleton className="h-7 w-40" />
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className={cn('h-[106px] w-full md:h-[176px]', CARD_RADIUS)} />
      ))}
    </div>
  );
}
